import type { CreativeBriefResponse } from '@innovationdepartment/proxima-sdk-axios';
import { useState } from 'react';
import AccordionSections from './components/AccordionSections';
import AssetPreview from './components/AssetPreview';
import BuyerInsightsSection from './components/BuyerInsightsSection';
import ConceptSection from './components/ConceptSection';
import DownloadDropdown from './components/DownloadDropdown';
import {
  AudienceGrid,
  AudienceKey,
  AudienceRow,
  AudienceValue,
  BriefCard,
  CardDivider,
  CardHeader,
  CardSection,
  CardSectionLabel,
  CardTitle,
  DesignArea,
  ImageColumn,
  LoadingArea,
  LoadingText,
  PanelActions,
  PanelBody,
  PanelCloseButton,
  PanelContainer,
  PanelHeader,
  PanelHeaderRow,
  PanelOverlay,
  PanelTitle,
  RightColumn,
  ShowMoreButton,
  TagChip,
  TagsRow,
  TopPanelArea
} from './styles/briefPanel.styles';

interface BriefPanelViewProps {
  brief: CreativeBriefResponse | null;
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean;
}

/** Format income range for display e.g. "$150K+" */
const formatIncome = (range: { min: number | null; max: number | null } | null): string | null => {
  if (!range) return null;
  const fmt = (n: number) => (n >= 1000 ? `$${Math.round(n / 1000)}K` : `$${n}`);
  if (range.min != null && range.max != null) return `${fmt(range.min)}–${fmt(range.max)}`;
  if (range.min != null) return `${fmt(range.min)}+`;
  if (range.max != null) return `Up to ${fmt(range.max)}`;
  return null;
};

const BriefPanelView = ({ brief, isOpen, onClose, loading }: BriefPanelViewProps) => {
  const [isClamped, setIsClamped] = useState(true);

  // Target audience key-value pairs from audienceInfo
  const audienceRows: Array<{ key: string; value: string }> = [];
  if (brief?.audienceInfo) {
    const ai = brief.audienceInfo;
    if (ai.gender) audienceRows.push({ key: 'Gender', value: ai.gender });
    if (ai.metro) audienceRows.push({ key: 'Metro', value: ai.metro });
    if (ai.city && !ai.metro) audienceRows.push({ key: 'City', value: ai.city });
    const incomeStr = formatIncome(ai.incomeRange);
    if (incomeStr) audienceRows.push({ key: 'Income', value: incomeStr });
    if (ai.ethnicity) audienceRows.push({ key: 'Ethnicity', value: ai.ethnicity });
  }

  // Header chips: names from brief payload
  const tagChips = brief
    ? [brief.productName, brief.strategyName, brief.format, brief.aspectRatio].filter(Boolean)
    : [];

  const renderBody = () => {
    if (loading) {
      return (
        <LoadingArea>
          <LoadingText>loading...</LoadingText>
        </LoadingArea>
      );
    }

    if (!brief) {
      return <TopPanelArea>Select a brief to view details</TopPanelArea>;
    }

    return (
      <DesignArea>
        <ImageColumn>
          <AssetPreview assets={brief.assets} title={brief.title} />
        </ImageColumn>

        <RightColumn>
          {/* Brief Summary card */}
          <BriefCard>
            <CardHeader>
              <CardTitle>Brief Summary</CardTitle>
              <ConceptSection conceptDescription={brief.conceptDescription} isClamped={isClamped} />
            </CardHeader>
            <ShowMoreButton onClick={() => setIsClamped(prev => !prev)}>
              {isClamped ? 'Show more' : 'Show less'}
            </ShowMoreButton>
            {audienceRows.length > 0 && (
              <>
                <CardDivider />
                <CardSection>
                  <CardSectionLabel>Target Audience</CardSectionLabel>
                  <AudienceGrid>
                    {audienceRows.map(({ key, value }) => (
                      <AudienceRow key={key}>
                        <AudienceKey>{key}</AudienceKey>
                        <AudienceValue>{value}</AudienceValue>
                      </AudienceRow>
                    ))}
                  </AudienceGrid>
                </CardSection>
              </>
            )}
          </BriefCard>

          {/* Audience Insights card */}
          {(brief.buyerInsights.location.length > 0 ||
            brief.buyerInsights.demographics.length > 0 ||
            brief.buyerInsights.brandSignals.length > 0) && (
            <BriefCard>
              <CardHeader>
                <CardTitle>Audience Insights</CardTitle>
              </CardHeader>
              <BuyerInsightsSection buyerInsights={brief.buyerInsights} />
            </BriefCard>
          )}

          {/* Messaging card */}
          <BriefCard>
            <CardHeader>
              <CardTitle>Messaging</CardTitle>
            </CardHeader>
            <AccordionSections brief={brief} />
          </BriefCard>
        </RightColumn>
      </DesignArea>
    );
  };

  return (
    <>
      <PanelOverlay isOpen={isOpen} onClick={onClose} aria-hidden="true" />
      <PanelContainer
        isOpen={isOpen}
        role={isOpen ? 'dialog' : undefined}
        aria-modal={isOpen ? 'true' : undefined}
        aria-hidden={isOpen ? undefined : 'true'}
        aria-label="Creative Brief"
      >
        <PanelHeader>
          <PanelHeaderRow>
            <PanelTitle>{brief?.title ?? '[Ad description]'}</PanelTitle>
            <PanelActions>
              {!!brief && <DownloadDropdown briefId={brief.briefId} assets={brief.assets} />}
              <PanelCloseButton onClick={onClose} aria-label="Close panel">
                &times;
              </PanelCloseButton>
            </PanelActions>
          </PanelHeaderRow>
          {tagChips.length > 0 && (
            <TagsRow>
              {tagChips.map(chip => (
                <TagChip key={chip}>{chip}</TagChip>
              ))}
            </TagsRow>
          )}
        </PanelHeader>

        <PanelBody>{renderBody()}</PanelBody>
      </PanelContainer>
    </>
  );
};

export default BriefPanelView;
